import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer 
} from 'recharts';
import { Card, Typography, Table } from 'antd';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const { Title, Text } = Typography;

// Cryptique Design System styles
const styles = {
  primaryColor: "#1d0c46",
  accentColor: "#caa968",
  textPrimary: "#111827",
  textSecondary: "#475569",
  bodyFont: "Raleway, sans-serif"
};

const EnhancedVisualization = ({ 
  type, 
  data, 
  columns, 
  config = {},
  height = 400,
  width = null // Optional width for responsive sizing
}) => {
  const flattenedData = data || [];

  // Function to format complex data for display
  const formatCellValue = (value) => {
    if (value === null || value === undefined) {
      return 'null';
    }
    
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? `[${value.length} items]` : '[]';
      }
      
      // For objects, show a summary
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return '{}';
      }
      
      // Show first few key-value pairs
      const preview = keys.slice(0, 2).map(key => {
        const val = value[key];
        if (typeof val === 'string' && val.length > 20) {
          return `${key}: "${val.substring(0, 20)}..."`;
        }
        return `${key}: ${val}`;
      }).join(', ');
      
      return keys.length > 2 ? `{${preview}, ...}` : `{${preview}}`;
    }
    
    if (typeof value === 'string' && value.length > 100) {
      return value.substring(0, 100) + '...';
    }
    
    return value;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const entry = payload[0];
      
      // For pie charts, payload structure is different
      // entry.payload contains the full data object with nameField and valueField
      if (entry.payload && entry.name && entry.value !== undefined) {
        // Pie chart tooltip
        const name = entry.name || entry.payload[Object.keys(entry.payload).find(key => key !== entry.dataKey)] || 'Unknown';
        const value = entry.value;
        const percent = entry.payload.percent ? (entry.payload.percent * 100).toFixed(1) : null;
        
        return (
          <div className="bg-white p-3 border rounded-none shadow-lg" style={{ fontFamily: styles.bodyFont }}>
            <p className="font-medium" style={{ color: styles.textPrimary, marginBottom: '4px' }}>
              {name}
            </p>
            <p style={{ color: entry.color || styles.primaryColor }}>
              {`Value: ${value}`}
            </p>
            {percent !== null && (
              <p style={{ color: styles.textSecondary, fontSize: '12px', marginTop: '2px' }}>
                {`${percent}%`}
              </p>
            )}
          </div>
        );
      }
      
      // For line/bar charts
      return (
        <div className="bg-white p-3 border rounded-none shadow-lg" style={{ fontFamily: styles.bodyFont }}>
          {label && <p className="font-medium" style={{ color: styles.textPrimary, marginBottom: '4px' }}>{label}</p>}
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || styles.primaryColor }}>
              {`${entry.dataKey || entry.name || 'Value'}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Enhanced KPI component with trend indicators
  const EnhancedKPI = ({ value, label, color, showTrend, previousValue, fontSize, labelFontSize, padding }) => {
    const getTrendIcon = () => {
      if (!showTrend || previousValue === undefined) return null;
      
      const iconSize = Math.max(12, Math.min(16, fontSize * 0.5));
      const change = ((value - previousValue) / previousValue) * 100;
      if (change > 0) return <TrendingUp size={iconSize} className="text-green-500" />;
      if (change < 0) return <TrendingDown size={iconSize} className="text-red-500" />;
      return <Minus size={iconSize} className="text-[#475569]" />;
    };

    const getTrendText = () => {
      if (!showTrend || previousValue === undefined) return '';
      
      const change = ((value - previousValue) / previousValue) * 100;
      return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
    };

    return (
      <div 
        className="text-center h-full"
        style={{ 
          borderColor: color, 
          borderWidth: '2px', 
          borderRadius: 0, 
          boxShadow: 'none',
          borderStyle: 'solid',
          padding: `${padding}px`,
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden'
        }}
      >
        <Text 
          type="secondary" 
          style={{ 
            width: '100%', 
            fontSize: `${labelFontSize}px`,
            marginBottom: `${padding * 0.5}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {label}
        </Text>
        <div
          style={{ 
            color, 
            margin: `${padding * 0.5}px 0`, 
            width: '100%',
            fontSize: `${fontSize}px`,
            fontWeight: 'bold',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : String(value)}
        </div>
        {showTrend && (
          <div className="flex items-center justify-center gap-1" style={{ width: '100%', marginTop: `${padding * 0.5}px` }}>
            {getTrendIcon()}
            <Text style={{ fontSize: `${labelFontSize * 0.85}px`, fontWeight: 500 }}>{getTrendText()}</Text>
          </div>
        )}
      </div>
    );
  };

  // Helper function to validate and get available columns from data
  const getAvailableColumns = () => {
    if (!flattenedData || flattenedData.length === 0) return columns || [];
    return Object.keys(flattenedData[0] || {});
  };

  // Helper function to validate column exists in data
  const validateColumn = (columnName, fallbackColumn) => {
    const availableColumns = getAvailableColumns();
    return availableColumns.includes(columnName) ? columnName : fallbackColumn;
  };

  const renderVisualization = () => {

    switch (type) {
      case 'Line Chart':
        const lineConfig = config;
        const availableColumns = getAvailableColumns();
        const xAxisColumn = validateColumn(lineConfig.xAxis, availableColumns[0]);
        const yAxisColumn = validateColumn(lineConfig.yAxis, availableColumns[1] || availableColumns[0]);
        
        const lineType = lineConfig.lineType || 'monotone';
        const strokeWidth = lineConfig.strokeWidth || 2;
        const showGrid = lineConfig.showGrid !== false;
        const lineColors = lineConfig.colors || [styles.primaryColor, styles.accentColor, '#8b5cf6', '#10b981', '#f59e0b'];
        
        // Determine data series - if Y-axis is configured, use only that column
        // If X and Y are the same, we need to create a different data structure
        const dataSeries = lineConfig.yAxis ? [yAxisColumn] : availableColumns.slice(1);
        const showLegend = lineConfig.showLegend !== false && dataSeries.length > 1;
        
        // Special handling when X and Y axis are the same column
        let chartData = flattenedData;
        if (xAxisColumn === yAxisColumn) {
          // When X and Y are the same, create a simple index-based chart
          chartData = flattenedData.map((row, index) => ({
            index: index + 1,
            [yAxisColumn]: row[yAxisColumn] || 0
          }));
        }
        
        return (
          <div className="relative">
            <ResponsiveContainer width="100%" height={height}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 50, bottom: 40 }}>
                {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
                <XAxis 
                  dataKey={xAxisColumn === yAxisColumn ? 'index' : xAxisColumn} 
                  tick={{ fontSize: 12, fill: styles.textSecondary }}
                  axisLine={{ stroke: styles.textSecondary }}
                  label={{ 
                    value: lineConfig.xAxisLabel || (xAxisColumn === yAxisColumn ? 'Index' : xAxisColumn), 
                    position: 'insideBottom', 
                    offset: -10,
                    style: { textAnchor: 'middle', fontSize: '14px', fill: styles.textPrimary, fontWeight: '500' }
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: styles.textSecondary }}
                  axisLine={{ stroke: styles.textSecondary }}
                  label={{ 
                    value: lineConfig.yAxisLabel || yAxisColumn || 'Value', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -15,
                    style: { 
                      textAnchor: 'middle', 
                      fontSize: '14px', 
                      fill: '#111827', 
                      fontWeight: '500',
                      transform: 'translateY(-50%)'
                    }
                  }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                {showLegend && <Legend />}
                {dataSeries.map((col, index) => (
                  <Line
                    key={col}
                    type={lineType}
                    dataKey={col}
                    stroke={lineColors[index % lineColors.length]}
                    strokeWidth={strokeWidth}
                    dot={{ r: 4, fill: lineColors[index % lineColors.length] }}
                    activeDot={{ r: 6, fill: lineColors[index % lineColors.length] }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
            {/* Fallback Y-axis label */}
            <div 
              className="absolute left-2 top-1/2 transform -translate-y-1/2 -rotate-90 text-sm font-medium text-[#0f172a]"
              style={{ 
                fontSize: '14px',
                fontWeight: '500',
                color: '#111827',
                whiteSpace: 'nowrap'
              }}
            >
              {lineConfig.yAxisLabel || yAxisColumn || 'Value'}
            </div>
          </div>
        );

      case 'Bar Chart':
        const barConfig = config;
        const barAvailableColumns = getAvailableColumns();
        const barXAxisColumn = validateColumn(barConfig.xAxis, barAvailableColumns[0]);
        const barYAxisColumn = validateColumn(barConfig.yAxis, barAvailableColumns[1] || barAvailableColumns[0]);
        const barRadius = barConfig.barRadius || 4;
        const barShowGrid = barConfig.showGrid !== false;
        const barColors = barConfig.colors || [styles.primaryColor, styles.accentColor, '#8b5cf6', '#10b981', '#f59e0b'];
        
        // Determine if we should show legend based on number of data series
        const barDataSeries = barConfig.yAxis ? [barYAxisColumn] : barAvailableColumns.slice(1);
        const barShowLegend = barConfig.showLegend !== false && barDataSeries.length > 1;
        
        // Special handling when X and Y axis are the same column
        let barChartData = flattenedData;
        if (barXAxisColumn === barYAxisColumn) {
          // When X and Y are the same, create a simple index-based chart
          barChartData = flattenedData.map((row, index) => ({
            index: index + 1,
            [barYAxisColumn]: row[barYAxisColumn] || 0
          }));
        }
        
        return (
          <div className="relative">
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 50, bottom: 40 }}>
                {barShowGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />}
                <XAxis 
                  dataKey={barXAxisColumn === barYAxisColumn ? 'index' : barXAxisColumn} 
                  tick={{ fontSize: 12, fill: styles.textSecondary }}
                  axisLine={{ stroke: styles.textSecondary }}
                  label={{ 
                    value: barConfig.xAxisLabel || (barXAxisColumn === barYAxisColumn ? 'Index' : barXAxisColumn), 
                    position: 'insideBottom', 
                    offset: -10,
                    style: { textAnchor: 'middle', fontSize: '14px', fill: styles.textPrimary, fontWeight: '500' }
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: styles.textSecondary }}
                  axisLine={{ stroke: styles.textSecondary }}
                  label={{ 
                    value: barConfig.yAxisLabel || barYAxisColumn || 'Value', 
                    angle: -90, 
                    position: 'insideLeft',
                    offset: -15,
                    style: { 
                      textAnchor: 'middle', 
                      fontSize: '14px', 
                      fill: '#111827', 
                      fontWeight: '500',
                      transform: 'translateY(-50%)'
                    }
                  }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                {barShowLegend && <Legend />}
                {barDataSeries.map((col, index) => (
                  <Bar
                    key={col}
                    dataKey={col}
                    fill={barColors[index % barColors.length]}
                    radius={[barRadius, barRadius, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {/* Fallback Y-axis label */}
            <div 
              className="absolute left-2 top-1/2 transform -translate-y-1/2 -rotate-90 text-sm font-medium text-[#0f172a]"
              style={{ 
                fontSize: '14px',
                fontWeight: '500',
                color: '#111827',
                whiteSpace: 'nowrap'
              }}
            >
              {barConfig.yAxisLabel || barYAxisColumn || 'Value'}
            </div>
          </div>
        );

      case 'Pie Chart':
        const pieConfig = config || {};
        const nameField = pieConfig.nameField;
        const valueField = pieConfig.valueField;
        
        // Validate that both fields are selected
        if (!nameField || !valueField || !columns.includes(nameField) || !columns.includes(valueField)) {
          return (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: styles.textSecondary,
              fontFamily: styles.bodyFont,
              textAlign: 'center',
              padding: '20px'
            }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: styles.primaryColor }}>
                  Pie Chart Configuration Required
                </div>
                <div style={{ fontSize: '14px' }}>
                  Please configure the Category and Value fields in the chart settings.
                </div>
              </div>
            </div>
          );
        }
        
        // Aggregate data: sum values for each unique category name
        const aggregatedData = {};
        flattenedData.forEach(row => {
          const categoryName = row[nameField];
          const value = parseFloat(row[valueField]) || 0;
          
          if (categoryName !== undefined && categoryName !== null) {
            const categoryKey = String(categoryName);
            if (aggregatedData[categoryKey]) {
              aggregatedData[categoryKey] += value;
            } else {
              aggregatedData[categoryKey] = value;
            }
          }
        });
        
        // Convert aggregated data to array format for Recharts
        const pieData = Object.keys(aggregatedData).map(categoryName => ({
          [nameField]: categoryName,
          [valueField]: aggregatedData[categoryName]
        }));
        
        // If no valid data, show empty state
        if (pieData.length === 0) {
          return (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: styles.textSecondary,
              fontFamily: styles.bodyFont
            }}>
              No data available for pie chart
            </div>
          );
        }
        
        const innerRadius = pieConfig.innerRadius || 0;
        const outerRadius = pieConfig.outerRadius || 80;
        const showLabels = pieConfig.showLabels !== false;
        const showPercentages = pieConfig.showPercentages !== false;
        
        // Get colors: use sliceColors map if available, otherwise fall back to colors array
        const sliceColors = pieConfig.sliceColors || {};
        const defaultColors = pieConfig.colors || [styles.primaryColor, styles.accentColor, '#8b5cf6', '#10b981', '#f59e0b'];
        
        // Function to get color for a category
        const getColorForCategory = (categoryName, index) => {
          return sliceColors[categoryName] || defaultColors[index % defaultColors.length];
        };
        
        const labelFormatter = ({ name, percent, value }) => {
          if (!showLabels) return '';
          if (showPercentages) {
            return `${name} ${(percent * 100).toFixed(0)}%`;
          }
          return `${name}: ${value}`;
        };
        
        // Custom tooltip for pie chart that properly handles the data structure
        const PieChartTooltip = ({ active, payload }) => {
          if (active && payload && payload.length) {
            const entry = payload[0];
            const categoryName = entry.payload[nameField] || entry.name || 'Unknown';
            const value = entry.value || entry.payload[valueField] || 0;
            const percent = entry.payload.percent ? (entry.payload.percent * 100).toFixed(1) : null;
            
            return (
              <div className="bg-white p-3 border rounded-none shadow-lg" style={{ fontFamily: styles.bodyFont }}>
                <p className="font-medium" style={{ color: styles.textPrimary, marginBottom: '4px' }}>
                  {categoryName}
                </p>
                <p style={{ color: entry.color || styles.primaryColor }}>
                  {`Value: ${value}`}
                </p>
                {percent !== null && (
                  <p style={{ color: styles.textSecondary, fontSize: '12px', marginTop: '2px' }}>
                    {`${percent}%`}
                  </p>
                )}
              </div>
            );
          }
          return null;
        };
        
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={showLabels ? labelFormatter : false}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                fill="#8884d8"
                dataKey={valueField}
                nameKey={nameField}
              >
                {pieData.map((entry, index) => {
                  const categoryName = entry[nameField];
                  const color = getColorForCategory(categoryName, index);
                  return <Cell key={`cell-${index}`} fill={color} />;
                })}
              </Pie>
              <RechartsTooltip content={<PieChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'KPI':
        const kpiConfig = config || {};
        // Respect exactly what the user selected - no forced overrides
        let valueColumns = kpiConfig.valueColumns;
        if (!valueColumns || !Array.isArray(valueColumns) || valueColumns.length === 0) {
          // No config: use first available column as default (single KPI card)
          valueColumns = columns.length > 0 ? [columns[0]] : [];
        }
        // Use exactly what's configured, no limits or overrides
        const layout = kpiConfig.layout || 'horizontal';
        const showTrend = kpiConfig.showTrend || false;
        const kpiColors = kpiConfig.colors || [styles.primaryColor];
        // Use the first color for all KPI cards (the one selected in the popup)
        const kpiColor = kpiColors[0] || styles.primaryColor;
        const customLabels = kpiConfig.customLabels || {};
        
        const kpiCount = valueColumns.length;
        
        // If no columns available, show empty state
        if (kpiCount === 0 || !flattenedData || flattenedData.length === 0) {
          return (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              color: styles.textSecondary,
              fontFamily: styles.bodyFont
            }}>
              No data available
            </div>
          );
        }
        
        // Calculate responsive sizing based on widget dimensions
        const containerWidth = width || 600;
        const containerHeight = height || 400;
        
        // Calculate gap based on container size
        const gap = Math.max(8, Math.min(16, Math.min(containerWidth, containerHeight) * 0.02));
        
        // Calculate responsive font sizes
        const baseSize = kpiCount > 0 ? Math.min(containerWidth / kpiCount, containerHeight) : containerHeight;
        const fontSize = Math.max(16, Math.min(48, baseSize * 0.2));
        const labelFontSize = Math.max(12, Math.min(16, fontSize * 0.4));
        const padding = Math.max(12, Math.min(24, baseSize * 0.05));
        
        // Determine layout style based on layout type
        const getLayoutStyle = () => {
          switch (layout) {
            case 'vertical':
              return {
                display: 'flex',
                flexDirection: 'column',
                gap: `${gap}px`,
                height: '100%',
                width: '100%'
              };
            case 'grid':
              const cols = kpiCount === 1 ? 1 : kpiCount <= 4 ? 2 : 3;
              return {
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: `${gap}px`,
                height: '100%',
                width: '100%'
              };
            default:
              // Horizontal layout
              return {
                display: 'flex',
                flexDirection: 'row',
                gap: `${gap}px`,
                height: '100%',
                width: '100%',
                flexWrap: kpiCount > 4 ? 'wrap' : 'nowrap'
              };
          }
        };
        
        return (
          <div style={{ 
            ...getLayoutStyle(),
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            {valueColumns.map((col, index) => {
              // Get value from first row of data
              const value = flattenedData[0]?.[col];
              // Use custom label if set, otherwise use column name
              const label = (customLabels[col] && customLabels[col].trim()) ? customLabels[col] : col;
              // For trend calculation, compare with previous row if available
              const previousValue = flattenedData.length > 1 ? flattenedData[1]?.[col] : undefined;
              
              // Format the value for display
              const displayValue = value !== undefined && value !== null ? value : 'N/A';
              
              return (
                <EnhancedKPI
                  key={`${col}-${index}`}
                  value={displayValue}
                  label={label}
                  color={kpiColor}
                  showTrend={showTrend}
                  previousValue={previousValue}
                  fontSize={fontSize}
                  labelFontSize={labelFontSize}
                  padding={padding}
                />
              );
            })}
          </div>
        );

      case 'Table':
        const tableConfig = config || {};
        // Build column config map for quick lookup
        const columnConfigMap = (tableConfig.columns || []).reduce((acc, c) => {
          acc[c.key] = c;
          return acc;
        }, {});

        const headerBg = tableConfig.headerColor || '#ffffff';
        const headerText = tableConfig.headerTextColor || '#1F2937';
        const zebra = tableConfig.zebraStripes === true;
        const pageSize = tableConfig.pageSize || Math.max(5, Math.floor((height - 120) / 32));

        return (
          <div style={{ 
            flex: 1,
            width: '100%', 
            height: '100%', 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxWidth: '100%',
            minWidth: 0,
            position: 'relative',
            borderRadius: 0,
            boxSizing: 'border-box'
          }}>
            <div style={{
              flex: 1,
              width: '100%',
              maxWidth: '100%',
              overflow: 'auto',
              minWidth: 0,
              boxSizing: 'border-box'
            }}>
              <Table
              dataSource={flattenedData}
              rowKey={(row) => row.id || row._id || row.key || JSON.stringify(row)}
              columns={columns.map(col => {
                const cfg = columnConfigMap[col] || {};
                return {
                  title: (
                    <span style={{ 
                      color: headerText,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '12px',
                      letterSpacing: '0.05em'
                    }}>
                      {cfg.label || col}
                    </span>
                  ),
                  dataIndex: col,
                  key: col,
                  width: 200,
                  minWidth: 150,
                  maxWidth: 300,
                  render: (value) => (
                    <span 
                      title={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      style={{ 
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        color: cfg.color || undefined,
                        userSelect: 'text',
                        cursor: 'text',
                        maxWidth: '100%'
                      }}
                    >
                      {String(value)}
                    </span>
                  ),
                  ellipsis: true
                };
              })}
              pagination={{ 
                pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                size: 'small',
                pageSizeOptions: ['5', '10', '20', '50', '100']
              }}
              scroll={{ 
                x: 'max-content',
                y: Math.max(200, height - 120)
              }}
              size="small"
              style={{ 
                width: '100%',
                maxWidth: '100%',
                flex: 1,
                minWidth: 0,
                tableLayout: 'fixed',
                borderRadius: 0
              }}
              components={{
                header: {
                  cell: (props) => (
                    <th {...props} style={{ ...(props.style || {}), background: headerBg, borderRadius: 0 }} />
                  )
                },
                body: {
                  cell: (props) => (
                    <td {...props} style={{ ...(props.style || {}), borderRadius: 0 }} />
                  ),
                  row: zebra ? (props) => {
                    const index = props['data-row-key'];
                    return <tr {...props} style={{ ...(props.style || {}), borderRadius: 0 }} />;
                  } : undefined
                }
              }}
              />
            </div>
          </div>
        );

      default:
        return <div>Unsupported visualization type: {type}</div>;
    }
  };

  return (
    <>
      <style>
        {`
          .ant-table-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            overflow: hidden !important;
            border-radius: 0 !important;
          }
          .ant-table-wrapper * {
            border-radius: 0 !important;
          }
          .ant-table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: fixed !important;
            border-radius: 0 !important;
            border: 0.5px solid #E5E7EB !important;
          }
          .ant-table-container {
            border-radius: 0 !important;
            border: 0.5px solid #E5E7EB !important;
          }
          .ant-table-content {
            border-radius: 0 !important;
          }
          .ant-table-body {
            border-radius: 0 !important;
          }
          .ant-table-tbody > tr > td {
            max-width: 200px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            border-radius: 0 !important;
            border: 0.5px solid #E5E7EB !important;
            border-top: 0.5px solid #E5E7EB !important;
            border-left: 0.5px solid #E5E7EB !important;
            border-right: 0.5px solid #E5E7EB !important;
            border-bottom: 0.5px solid #E5E7EB !important;
          }
          .ant-table-thead > tr > th {
            max-width: 200px !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            border-radius: 0 !important;
            border: 0.5px solid #E5E7EB !important;
            border-top: 0.5px solid #E5E7EB !important;
            border-left: 0.5px solid #E5E7EB !important;
            border-right: 0.5px solid #E5E7EB !important;
            border-bottom: 0.5px solid #E5E7EB !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            font-size: 12px !important;
            letter-spacing: 0.05em !important;
          }
          .ant-table-thead > tr > th:first-child {
            border-top-left-radius: 0 !important;
            border-radius: 0 !important;
          }
          .ant-table-thead > tr > th:last-child {
            border-top-right-radius: 0 !important;
            border-radius: 0 !important;
          }
          .ant-table-tbody > tr:last-child > td:first-child {
            border-bottom-left-radius: 0 !important;
            border-radius: 0 !important;
          }
          .ant-table-tbody > tr:last-child > td:last-child {
            border-bottom-right-radius: 0 !important;
            border-radius: 0 !important;
          }
          .ant-table-tbody > tr > td:first-child {
            border-radius: 0 !important;
          }
          .ant-table-tbody > tr > td:last-child {
            border-radius: 0 !important;
          }
        `}
      </style>
      <div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        maxWidth: '100%',
        minWidth: 0
      }}>
        {renderVisualization()}
      </div>
    </>
  );
};

export default EnhancedVisualization;
